import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { Accordion } from '@uhg-abyss/web/ui/Accordion';
import { usePropsContext } from '@uhg-abyss/web/hooks/usePropsContext';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { ProviderTableData } from '../../../common/ProviderTableData';
import { ProviderTableSearch } from '../../../common/ProviderTableSearch';
import {
  StyledAccordionContent,
  StyledAccordionTrigger,
  StyledAccordionHeader,
  StyledTableRow,
  StyledTableDataCheckMarkColumn,
  StyledTableDataLabelColumn,
  StyledErrorMessage,
} from '../../../common/StyledComponents/StyledComponents';

const taxIdNumberColWidths = ['10%', '90%'];

const errorMessage = 'Unable to load tax ID number';

export const TaxIdNumbers = ({
  selectedProvider,
  setSelectedProvider,
  organizations,
  setExpandedValue,
  isTaxIdNumbersLoading,
  isOwnerRowClicked,
  setIsOwnerRowClicked,
  setIsTaxIdRowClicked,
  organizationsError,
  isTaxIdAccordionExpanded,
  setIsSaveDisabled,
  setCanCallProviders,
  isAccordionDisabled,
  allowProviderSearch,
  setIsCareProviderAccordionDisabled,
}) => {
  const { events } = usePropsContext();
  const { taxIdNumber } = selectedProvider;
  const [taxIdSearchValue, setTaxIdSearchValue] = useState('');
  const [selectedTaxId, setSelectedTaxId] = useState(taxIdNumber);
  const [selectedTaxIdRow, setSelectedTaxIdRow] = useState(taxIdNumber);

  useEffect(() => {
    if (selectedTaxId && allowProviderSearch) {
      setCanCallProviders(true);
    }
  }, [selectedTaxId, allowProviderSearch]);

  useEffect(() => {
    if (!taxIdNumber) {
      setSelectedTaxId('');
      setSelectedTaxIdRow('');
    }
  }, [selectedProvider]);

  useEffect(() => {
    if (isOwnerRowClicked) {
      setSelectedTaxId('');
      setSelectedTaxIdRow('');
      setTaxIdSearchValue('');
    }
  }, [isOwnerRowClicked, selectedProvider.corporateProviderMpin]);

  useEffect(() => {
    if (!selectedTaxId) {
      setIsCareProviderAccordionDisabled(true);
    } else {
      setIsCareProviderAccordionDisabled(false);
    }
  }, [selectedTaxId]);

  const filteredTaxIdData = organizations.filter((item) =>
    item.tin.includes(taxIdSearchValue)
  );

  useEffect(() => {
    if (isOwnerRowClicked && filteredTaxIdData.length > 1) {
      setCanCallProviders(false);
    }
  }, [isOwnerRowClicked]);

  let subText = '';
  if (organizationsError && !isTaxIdAccordionExpanded) {
    subText = (
      <Layout.Group>
        <IconSymbol icon='error' color='$error1' variant='outlined' />
        <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
      </Layout.Group>
    );
  } else if (!organizationsError) {
    subText = selectedTaxId;
  }

  const handleTaxIdChange = (taxId) => {
    if (selectedProvider?.taxIdNumber !== taxId) {
      const orgTinMatch = (organizations || []).find(
        (org) => org.tin === taxId
      );

      setSelectedProvider((prevProvider) => {
        if (prevProvider.taxIdNumber === taxId) {
          return prevProvider;
        }
        return {
          ...selectedProvider,
          organizationId: orgTinMatch?.id || '',
          organizationName: orgTinMatch?.name || '',
          taxIdNumber: orgTinMatch?.tin,
          tinType: orgTinMatch?.tinType,
          allPermission: orgTinMatch?.allPermission || false,
          permissionCode: orgTinMatch?.permissionCode || [],
          permissionSubCode: orgTinMatch?.permissionSubCode || [],
          permissions: orgTinMatch?.privileges || [],
          privileges: orgTinMatch?.privileges || [],
          tpsm: orgTinMatch?.tpsm || [],
        };
      });
      events.onTaxIdNumberChange(orgTinMatch);
    }
  };

  const taxIdSelection = (tin) => {
    setIsTaxIdRowClicked(true);
    setSelectedTaxIdRow(tin);
    setSelectedTaxId(tin);
    setExpandedValue('care-provider');
    setIsOwnerRowClicked(false);
    setTaxIdSearchValue('');
    setIsSaveDisabled(true);
    handleTaxIdChange(tin);
    setCanCallProviders(true);
  };

  const handleTaxIdRowClick = (tin) => {
    if (selectedTaxIdRow === tin) {
      return;
    }
    taxIdSelection(tin);
  };

  const renderRow = (data) => {
    if (isOwnerRowClicked && data && data.length === 1 && !taxIdSearchValue) {
      taxIdSelection(data[0].tin);
    }
    return data.map((item) => {
      return (
        <React.Fragment>
          <StyledTableRow
            key={item.tin}
            onClick={() => handleTaxIdRowClick(item.tin)}
          >
            <StyledTableDataCheckMarkColumn>
              <If condition={selectedTaxIdRow === item.tin}>
                <IconMaterial size='22px' icon='check' />
              </If>
            </StyledTableDataCheckMarkColumn>
            <StyledTableDataLabelColumn
              data-testid='tax-id-number-table-row'
              isSelectedRow={selectedTaxIdRow === item.tin}
            >
              {item.tin}
            </StyledTableDataLabelColumn>
          </StyledTableRow>
        </React.Fragment>
      );
    });
  };

  return (
    <React.Fragment>
      <StyledAccordionTrigger isDisabled={isAccordionDisabled}>
        <StyledAccordionHeader isError={organizationsError} subText={subText}>
          Tax ID number
        </StyledAccordionHeader>
      </StyledAccordionTrigger>
      <Accordion.Content>
        <StyledAccordionContent>
          <ProviderTableSearch
            searchValue={taxIdSearchValue}
            setSearchValue={setTaxIdSearchValue}
            isTaxIdNumber
            isDisabled={isTaxIdNumbersLoading}
            isError={organizationsError}
          />
          <ProviderTableData
            colWidths={taxIdNumberColWidths}
            data={filteredTaxIdData}
            renderRow={renderRow}
            isLoading={isTaxIdNumbersLoading}
            ariaLoadingLabel='Loading tax ID numbers'
            isError={organizationsError}
            errorMessage={errorMessage}
          />
        </StyledAccordionContent>
      </Accordion.Content>
    </React.Fragment>
  );
};

TaxIdNumbers.propTypes = {
  organizations: PropTypes.arrayOf(PropTypes.shape({})),
  selectedProvider: PropTypes.shape({
    organizationId: PropTypes.string,
    corporateProviderMpin: PropTypes.string,
    taxIdNumber: PropTypes.string,
    uhcProviderId: PropTypes.string,
    userId: PropTypes.string,
    uniqueId: PropTypes.string,
  }),
  setSelectedProvider: PropTypes.func.isRequired,
  isTaxIdNumbersLoading: PropTypes.bool.isRequired,
  setExpandedValue: PropTypes.func.isRequired,
  isOwnerRowClicked: PropTypes.bool.isRequired,
  setIsOwnerRowClicked: PropTypes.func.isRequired,
  setIsTaxIdRowClicked: PropTypes.func.isRequired,
  organizationsError: PropTypes.bool,
  isTaxIdAccordionExpanded: PropTypes.bool.isRequired,
  setIsSaveDisabled: PropTypes.func.isRequired,
  setCanCallProviders: PropTypes.func,
  isAccordionDisabled: PropTypes.bool,
  allowProviderSearch: PropTypes.bool,
  setIsCareProviderAccordionDisabled: PropTypes.func,
};

TaxIdNumbers.defaultProps = {
  organizations: [],
  selectedProvider: null,
  organizationsError: false,
  setCanCallProviders: () => {},
  isAccordionDisabled: false,
  allowProviderSearch: false,
  setIsCareProviderAccordionDisabled: () => {},
};
